<template>
  <div v-if="show" class="dropdown acct">
    <button
      type="button"
      class="acct-pill"
      :class="{ 'is-sub': isSubContext, 'is-static': !canSwitch }"
      :data-bs-toggle="canSwitch ? 'dropdown' : null"
      aria-expanded="false"
      :aria-label="$t('account.activeAccount', { name: activeName }) + (canSwitch ? $t('account.switchHint') : '')"
    >
      <span class="acct-avatars">
        <span v-if="isSubContext" class="acct-av acct-av-parent" :title="brandName">
          <img v-if="brand?.logo" :src="brand.logo" alt="" />
          <span v-else>{{ initial(brand) }}</span>
        </span>
        <span class="acct-av acct-av-active">
          <img v-if="active?.logo" :src="active.logo" alt="" />
          <span v-else>{{ initial(active) }}</span>
        </span>
      </span>
      <span class="acct-text">
        <span class="acct-label">{{ isSubContext ? $t("account.subAccountOf", { name: brandName }) : $t("account.mainAccount") }}</span>
        <span class="acct-name">{{ activeName }}</span>
      </span>
      <i v-if="canSwitch" class="ph ph-caret-up-down acct-caret" aria-hidden="true"></i>
    </button>

    <div v-if="canSwitch" class="dropdown-menu acct-menu">
      <p class="acct-menu-title">{{ $t("account.switchAccount") }}</p>

      <button
        v-for="account in auth.accounts"
        :key="account.id"
        type="button"
        class="acct-item"
        :class="{ current: isCurrent(account) }"
        :disabled="entering !== null || isCurrent(account) || (!isRoot(account) && !account.active)"
        @click="enter(account)"
      >
        <span class="acct-av acct-av-sm" :class="{ root: isRoot(account) }">
          <img v-if="account.logo" :src="account.logo" alt="" />
          <span v-else>{{ initial(account) }}</span>
        </span>
        <span class="acct-item-text">
          <span class="acct-item-name">{{ account.name || account.store }}</span>
          <span class="acct-item-tag">
            {{ isRoot(account) ? $t("account.mainAccount") : account.active ? $t("account.subAccount") : $t("account.inactive") }}
          </span>
        </span>
        <span v-if="entering === account.id" class="spinner-border spinner-border-sm acct-item-end"></span>
        <i v-else-if="isCurrent(account)" class="ph ph-check acct-item-end acct-check" aria-hidden="true"></i>
      </button>

      <div class="acct-menu-foot">
        <router-link to="/choose-account" class="dropdown-item">
          <i class="ph ph-squares-four" aria-hidden="true"></i> {{ $t("account.allAccounts") }}
        </router-link>
        <router-link v-if="!auth.isInSubAccount" to="/sub-accounts" class="dropdown-item">
          <i class="ph ph-gear-six" aria-hidden="true"></i> {{ $t("account.manageSubAccounts") }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import { useAppToast } from "@/composables/useAppToast";

export default defineComponent({
  name: "AccountSwitcher",
  setup() {
    const { t } = useI18n();
    const auth = useAuthStore();
    const { showToast } = useAppToast();
    const entering = ref<number | string | null>(null);

    // Shown for a Business Account with sub accounts, and for a sub account
    // (logged in directly) so its parent's logo stays visible.
    const show = computed(() => !auth.isStaff && (auth.canSwitchAccounts || !!auth.parentAccount));
    const canSwitch = computed(() => auth.canSwitchAccounts);
    const brand = computed(() => auth.brandAccount);
    const active = computed(() => auth.user);
    const isSubContext = computed(
      () => !!brand.value && !!active.value && String(brand.value.id) !== String(active.value.id)
    );
    const brandName = computed(() => brand.value?.name || brand.value?.store || "");
    const activeName = computed(() => active.value?.name || active.value?.store || "");

    const isRoot = (a: Record<string, any>) => String(a.id) === String(auth.rootAccount?.id);
    const isCurrent = (a: Record<string, any>) => String(a.id) === String(auth.user?.id);
    const initial = (a: Record<string, any> | null) => (a?.name || a?.store || "?").charAt(0).toUpperCase();

    async function enter(account: Record<string, any>) {
      entering.value = account.id;
      try {
        await auth.enterAccount(account.id);
        // full reload so no data from the previous account stays in memory
        window.location.replace("/");
      } catch {
        showToast(t("account.enterFailed"), "error");
        entering.value = null;
      }
    }

    return {
      auth, entering, show, canSwitch, brand, active, isSubContext, brandName, activeName,
      isRoot, isCurrent, initial, enter,
    };
  },
});
</script>

<style scoped>
.acct-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 280px;
  padding: 4px 12px 4px 4px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  color: var(--ui-text);
  text-align: left;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), box-shadow var(--ui-dur-fast) var(--ui-ease),
    border-color var(--ui-dur-fast) var(--ui-ease);
}
.acct-pill:not(.is-static):hover,
.acct-pill[aria-expanded="true"] {
  border-color: var(--ui-accent);
  box-shadow: 0 6px 16px rgba(var(--ui-accent-rgb), 0.14);
}
.acct-pill.is-static {
  cursor: default;
}
.acct-pill.is-sub {
  border-color: rgba(var(--ui-accent-rgb), 0.5);
  background: rgba(var(--ui-accent-rgb), 0.06);
}

.acct-avatars {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}
.acct-av {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--ui-text);
  color: #fff;
  font-size: 14px;
  font-weight: var(--ui-fw-black);
}
.acct-av img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #fff;
}
/* parent brand behind, active sub account in front */
.acct-av-parent {
  width: 30px;
  height: 30px;
  font-size: 12px;
  background: var(--ui-accent);
  box-shadow: 0 0 0 2px var(--ui-surface);
}
.acct-av-parent + .acct-av-active {
  margin-left: -10px;
  box-shadow: 0 0 0 2px var(--ui-surface);
}
.acct-pill:not(.is-sub) .acct-av-active {
  background: var(--ui-accent);
}

.acct-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}
.acct-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-semibold);
}
.acct-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
}
.acct-caret {
  margin-left: 2px;
  color: var(--ui-text-muted);
  font-size: 14px;
}

.acct-menu {
  min-width: 280px;
  margin-top: 10px !important;
  padding: 6px;
}
.acct-menu-title {
  margin: 0;
  padding: 8px 10px 6px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.acct-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: var(--ui-radius-md);
  background: none;
  color: var(--ui-text);
  text-align: left;
}
.acct-item:hover:not(:disabled) {
  background: var(--ui-surface-sunken);
}
.acct-item.current {
  background: rgba(var(--ui-accent-rgb), 0.08);
}
.acct-item:disabled:not(.current) {
  opacity: 0.5;
}
.acct-av-sm {
  width: 32px;
  height: 32px;
  font-size: 13px;
  flex: 0 0 auto;
}
.acct-av-sm.root {
  background: var(--ui-accent);
}
.acct-item-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
}
.acct-item-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.acct-item-tag {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
}
.acct-item-end {
  margin-left: auto;
}
.acct-check {
  color: var(--ui-accent-text);
  font-size: 16px;
}
.acct-menu-foot {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--ui-border);
}
.acct-menu-foot .dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.acct-menu-foot .dropdown-item i {
  color: var(--ui-accent-text);
  font-size: 17px;
}

@media (max-width: 767.98px) {
  .acct-pill {
    padding: 3px;
  }
  .acct-text,
  .acct-caret {
    display: none;
  }
  .acct-av {
    width: 32px;
    height: 32px;
  }
}
</style>
